'use client';

import { useEffect, useRef } from 'react';

import { storageSearchedTermKey } from '@libs/analytics';

/**
 * SearchTermGuard
 *
 * Preserves search attribution while the user is interacting with the PDP
 * experience.
 *
 * `item_search_term` is stored in sessionStorage so downstream ecommerce
 * analytics events (view_item, add_to_cart, purchase, etc.) can be linked
 * back to the originating search query.
 *
 * The guard listens for document clicks and removes the stored search term
 * only when the user clicks a link outside the core PDP interaction zones:
 *
 *   - `.pdp__container`
 *   - `#product-description`
 *
 * Clicks inside those PDP zones are ignored.
 * Clicks outside those zones are also ignored unless they occur on a link.
 *
 * This prevents stale search attribution from leaking into unrelated browsing
 * journeys while avoiding accidental cleanup during normal PDP interactions.
 */
export function SearchTermGuard() {
    const pdpContainerRef = useRef<Element | null>(null);
    const productDescriptionRef = useRef<Element | null>(null);

    useEffect(() => {
        pdpContainerRef.current = document.querySelector('.pdp__container');
        productDescriptionRef.current = document.getElementById('product-description');

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as Element | null;

            if (!target) {
                return;
            }

            const linkElement = target.closest('a');

            if (!linkElement) {
                return;
            }

            const insidePdpContainer = pdpContainerRef.current?.contains(target) ?? false;
            const insideProductDescription = productDescriptionRef.current?.contains(target) ?? false;

            if (insidePdpContainer || insideProductDescription) {
                return;
            }

            sessionStorage.removeItem(storageSearchedTermKey);
        };

        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);

    return null;
}
